import { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalDialog, ModalClose, Typography, Box, List, ListItemButton,
  ListItemContent, Avatar, CircularProgress, IconButton, Alert, Button,
  Textarea, Stack, Divider, Chip, useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';

import { APIReviewMyDoctors, APIReviewCreate } from '../../api';

// Порог: если оценка <= COMMENT_REQUIRED_MAX — комментарий обязателен.
const COMMENT_REQUIRED_MAX = 3;
const RATING_HINTS = ['', 'Очень плохо', 'Плохо', 'Нормально', 'Хорошо', 'Отлично'];

export default function ReviewModal({ open, onClose, token, onSubmitted }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep] = useState('doctor'); // 'doctor' | 'rate'
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Сброс и загрузка врачей при открытии
  useEffect(() => {
    if (!open) return;
    setStep('doctor'); setError(''); setSelectedDoctor(null);
    setRating(0); setHover(0); setComment(''); setSubmitting(false);

    setLoading(true);
    APIReviewMyDoctors(token)
      .then((d) => {
        const list = d?.doctors || [];
        setDoctors(list);
        // если врач один — сразу к оценке (с подстановкой прежней оценки, если уже оценивал)
        if (list.length === 1) {
          setSelectedDoctor(list[0]);
          setRating(list[0].my_rating || 0);
          setStep('rate');
        }
      })
      .catch(() => setError('Не удалось загрузить список ваших врачей'))
      .finally(() => setLoading(false));
  }, [open, token]);

  const pickDoctor = (doc) => { setSelectedDoctor(doc); setRating(doc.my_rating || 0); setStep('rate'); };

  const commentRequired = rating > 0 && rating <= COMMENT_REQUIRED_MAX;
  const canSubmit = rating > 0 && (!commentRequired || comment.trim().length > 0) && !submitting;

  const submit = async () => {
    if (!selectedDoctor || !canSubmit) return;
    setSubmitting(true); setError('');
    try {
      const resp = await APIReviewCreate(token, {
        doctor_id: selectedDoctor.id,
        rating,
        comment: comment.trim(),
      });
      if (resp?.success) { onSubmitted?.(resp); onClose?.(); }
      else setError(resp?.error || 'Не удалось отправить оценку');
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось отправить оценку. Попробуйте позже.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeStars = hover || rating;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout={isMobile ? 'fullscreen' : 'center'} sx={isMobile ? {} : { maxWidth: 460, width: '100%' }}>
        <ModalClose />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {step === 'rate' && doctors.length > 1 && (
            <IconButton size="sm" variant="plain" onClick={() => setStep('doctor')}>
              <ArrowBackRoundedIcon />
            </IconButton>
          )}
          <Typography level="title-lg">
            {step === 'doctor'
              ? 'Оценить чат с доктором'
              : (selectedDoctor?.already_reviewed ? 'Изменить оценку' : 'Ваша оценка')}
          </Typography>
        </Box>

        {error && <Alert color="danger" variant="soft" sx={{ mb: 1 }}>{error}</Alert>}

        {/* Шаг 1: выбор врача */}
        {step === 'doctor' && (
          loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            : (
              <>
                <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 1 }}>
                  Выберите специалиста, чат с которым хотите оценить
                </Typography>
                <List sx={{ gap: 0.5, maxHeight: '60vh', overflow: 'auto' }}>
                  {doctors.length === 0 && (
                    <Alert color="neutral" variant="soft" sx={{ m: 1 }}>
                      Пока некого оценивать. Оценить можно специалиста, к которому вы подключены —
                      сначала подключитесь к врачу через «Подключиться к специалисту».
                    </Alert>
                  )}
                  {doctors.map((d) => (
                    <ListItemButton key={d.id} onClick={() => pickDoctor(d)} sx={{ borderRadius: 'sm' }}>
                      <Avatar size="sm" src={d.avatar_url || undefined} sx={{ mr: 1.5 }}>
                        {(d.name || '?').slice(0, 1)}
                      </Avatar>
                      <ListItemContent>
                        <Typography level="title-sm">{d.name}</Typography>
                        {Array.isArray(d.specialization) && d.specialization.length > 0 && (
                          <Typography level="body-xs" textColor="text.tertiary">
                            {d.specialization.map((s) => s.name || s).join(', ')}
                          </Typography>
                        )}
                        {d.avg_rating != null && d.reviews_count > 0 && (
                          <Chip size="sm" variant="soft" color="warning"
                            startDecorator={<StarRoundedIcon sx={{ fontSize: 14 }} />} sx={{ mt: 0.25 }}>
                            {Number(d.avg_rating).toFixed(1)} · {d.reviews_count}
                          </Chip>
                        )}
                        {d.already_reviewed && (
                          <Chip size="sm" variant="soft" color="success" sx={{ mt: 0.25, ml: 0.5 }}>
                            Вы оценили: {d.my_rating}★ — изменить
                          </Chip>
                        )}
                      </ListItemContent>
                    </ListItemButton>
                  ))}
                </List>
              </>
            )
        )}

        {/* Шаг 2: оценка */}
        {step === 'rate' && selectedDoctor && (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar size="sm" src={selectedDoctor.avatar_url || undefined}>
                {(selectedDoctor.name || '?').slice(0, 1)}
              </Avatar>
              <Box>
                <Typography level="title-sm">{selectedDoctor.name}</Typography>
                {Array.isArray(selectedDoctor.specialization) && selectedDoctor.specialization.length > 0 && (
                  <Typography level="body-xs" textColor="text.tertiary">
                    {selectedDoctor.specialization.map((s) => s.name || s).join(', ')}
                  </Typography>
                )}
                {selectedDoctor.avg_rating != null && selectedDoctor.reviews_count > 0 && (
                  <Typography level="body-xs" textColor="text.tertiary">
                    Рейтинг: {Number(selectedDoctor.avg_rating).toFixed(1)} ({selectedDoctor.reviews_count})
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Звёзды */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }} onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((i) => {
                  const filled = i <= activeStars;
                  return (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.18 }}
                      whileTap={{ scale: 0.9 }}
                      animate={{ scale: i === activeStars ? 1.12 : 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                    >
                      <IconButton
                        variant="plain" color="warning" size="lg"
                        sx={{ p: 0.5, '--IconButton-size': '44px' }}
                        onMouseEnter={() => setHover(i)}
                        onClick={() => setRating(i)}
                        aria-label={`Оценка ${i}`}
                      >
                        {filled
                          ? <StarRoundedIcon sx={{ fontSize: 40, color: 'var(--joy-palette-warning-400)' }} />
                          : <StarBorderRoundedIcon sx={{ fontSize: 40, color: 'var(--joy-palette-neutral-300)' }} />}
                      </IconButton>
                    </motion.div>
                  );
                })}
              </Box>
              <Typography level="body-sm" textColor="text.tertiary" sx={{ minHeight: 20 }}>
                {RATING_HINTS[activeStars] || 'Нажмите на звёзды, чтобы оценить'}
              </Typography>
            </Box>

            {/* Комментарий */}
            <Box>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>
                Комментарий{commentRequired ? ' (обязательно)' : ' (по желанию)'}
              </Typography>
              <Textarea
                minRows={3} maxRows={6}
                placeholder={commentRequired
                  ? 'Расскажите, что пошло не так — это поможет нам стать лучше'
                  : 'Поделитесь впечатлением о чате с доктором'}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                color={commentRequired && !comment.trim() ? 'warning' : 'neutral'}
              />
              {commentRequired && !comment.trim() && (
                <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                  При оценке 3 звезды и ниже комментарий обязателен
                </Typography>
              )}
            </Box>

            <Button loading={submitting} disabled={!canSubmit} onClick={submit}>
              {selectedDoctor?.already_reviewed ? 'Изменить оценку' : 'Отправить оценку'}
            </Button>
          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
}